import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apigetHomeTestseriesDetails, checkPurchaseStatus, createOrder, getUserTestAttempts, verifyPayment } from "../../services/apihomeTestseries/apihometestseries";
import { useNavigate } from "react-router-dom";
import useAuth from "../../services/store/useAuth";
import { Lock, X, ShoppingCart } from "lucide-react";
// import { createOrder, verifyPayment, checkPurchaseStatus } from "../../services/apiOrder/apiOrder";

export default function Testdetailpage() {
  const { slug } = useParams();
  const { userdetails } = useAuth();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const navigate = useNavigate();
  const [testAttempts, setTestAttempts] = useState({});
  const [resultsModal, setResultsModal] = useState(false);
  

  useEffect(() => {
    const fetchTestDetails = async () => {
      try {
        const res = await apigetHomeTestseriesDetails(slug);
        if (res?.resdata?.[0]) {
          setTest(res.resdata[0]);
          
          if (userdetails?.id) {
            const purchaseStatus = await checkPurchaseStatus(userdetails.id, slug);
            setHasPurchased(purchaseStatus.purchased);

            const attemptsRes = await getUserTestAttempts(userdetails.id, slug);
            if (attemptsRes.success) {
              const attemptsMap = {};
              attemptsRes.attempts.forEach(attempt => {
                if (!attemptsMap[attempt.test_id]) {
                  attemptsMap[attempt.test_id] = [];
                }
                attemptsMap[attempt.test_id].push(attempt);
              });
              setTestAttempts(attemptsMap);
            }
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTestDetails();
  }, [slug, userdetails]);

  const isFree = (price, salePrice) => {
    return parseInt(price) === 0 && parseInt(salePrice) === 0;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

const handleBuyNow = async () => {
  if (!userdetails) {
    navigate('/login');
    return;
  }

  if (hasPurchased) {
    alert('You have already purchased this test series');
    return;
  }

  setPurchasing(true);

  try {
    const orderData = await createOrder(slug, userdetails.id,
       {
        user_name: userdetails.First_Name || userdetails.Full_name || '',
        user_email: userdetails.Email || '',
        user_type: userdetails.Role === 'Student' ? 'students' : 'customers'
      }
    );

    if (orderData.is_free) {
      alert('Free test series activated successfully!');
      setHasPurchased(true);
      window.location.reload();
      return;
    }

    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      alert('Failed to load payment gateway. Please try again.');
      setPurchasing(false);
      return;
    }

    const options = {
      key: orderData.key_id,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'Test Series Purchase',
      description: test.Test_name,
      order_id: orderData.order_id,
      handler: async (response) => {
        try {
          const verifyData = await verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });

          if (verifyData.success) {
            alert('Payment successful! You can now access all tests.');
            setHasPurchased(true);
            window.location.reload();
          }
        } catch (error) {
          alert('Payment verification failed. Please contact support.');
          console.error('Payment verification error:', error);
        }
      },
      prefill: {
        name: userdetails.First_Name || '',
        email: userdetails.Email || '',
      },
      theme: {
        color: '#1e40af'
      },
      modal: {
        ondismiss: () => {
          setPurchasing(false);
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
    setPurchasing(false);

  } catch (error) {
    console.error('Purchase error:', error);
    alert(error.message || 'Failed to initiate purchase. Please try again.');
    setPurchasing(false);
  }
};

  const handleStartTest = (testId) => {
    if (!hasPurchased) {
      alert('Please purchase this test series to access the tests');
      return;
    }
    navigate(`/online-test-series/${slug}/test/${testId}`);
  };

  if (loading) return <div className="py-20 text-center">Loading…</div>;
  if (!test) return null;
  
  const originalPrice = parseFloat(test.Price);
  const salePrice = parseFloat(test.Sale_Price);
  const savings = originalPrice - salePrice;

  return (
    <>
      <section className="bg-regal-blue">
        <div className="max-w-[85rem] mx-auto px-4 py-16">
          <div className="grid items-center gap-8 md:grid-cols-2">
           <div className="text-white">
              <h1 className="mb-4 text-4xl font-bold md:text-5xl">{test.Test_name}</h1>
              
              {isFree(test.Price, test.Sale_Price) ? (
                <div className="mb-6">
                  <span className="inline-block px-6 py-3 text-3xl font-bold text-green-600 bg-green-100 rounded-full">
                    FREE
                  </span>
                  <p className="mt-3 text-lg opacity-90">Access this test series at no cost</p>
                </div>
              ) : (
                <div className="flex items-baseline gap-4 mb-6">
                  <span className="text-5xl font-bold text-regal-yellow">₹{salePrice}</span>
                  <span className="text-2xl line-through opacity-70">₹{originalPrice}</span>
                  <span className="px-3 py-1 text-sm font-semibold rounded-full bg-regal-yellow text-regal-blue">
                    Save ₹{savings}
                  </span>
                </div>
              )}
              
              {hasPurchased ? (
                <div className="flex justify-between px-6 py-3 text-lg font-bold text-white bg-green-500 rounded-lg">
                  <p>✓ Purchased</p>
                  <Link to={"/myorder"} className="px-3 py-1 text-sm bg-red-600 rounded-2xl">See My Orders</Link>
                </div>
              ) : isFree(test.Price, test.Sale_Price) ? (
                <button onClick={handleBuyNow} disabled={purchasing}
                  className="flex items-center gap-2 px-8 py-3 text-lg font-bold text-white transition-opacity bg-green-600 rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  {purchasing ? 'Processing...' : 'Get Free Access'}
                </button>
              ) : (
                <button onClick={handleBuyNow} disabled={purchasing}
                  className="flex items-center gap-2 px-8 py-3 text-lg font-bold transition-opacity rounded-lg bg-regal-yellow text-regal-blue hover:opacity-90 disabled:opacity-50"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {purchasing ? 'Processing...' : 'Buy Now'}
                </button>
              )}
            </div>
            <div className="p-6 bg-white shadow-2xl rounded-xl">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 text-center rounded-lg bg-gray-50">
                  <div className="text-3xl font-bold text-regal-blue">{test.tests?.length || 0}</div>
                  <div className="mt-1 text-sm text-gray-600">Total Tests</div>
                </div>
              
                <div className="p-4 text-center rounded-lg bg-gray-50">
                  <div className="text-3xl font-bold text-regal-blue">
                    {test.tests?.[0]?.duration || 0}
                  </div>
                  <div className="mt-1 text-sm text-gray-600">Minutes/Test</div>
                </div>
                <div className="p-4 text-center rounded-lg bg-gray-50">
                  <div className="text-3xl font-bold text-regal-blue">{test.Discount}%</div>
                  <div className="mt-1 text-sm text-gray-600">Discount</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[85rem] mx-auto px-4 py-12">
        <div className="p-8 bg-white shadow-[0px_0px_29px_0px_rgba(0,_0,_0,_0.1)] rounded-xl border-regal-yellow">
          <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: test.Description }}></div>
        </div>
      </section>

      <section className="py-12 bg-gray-50">
        <div className="max-w-[85rem] mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-8 bg-regal-yellow"></div>
            <h2 className="text-3xl font-bold text-regal-blue">Tests Included</h2>
          </div>
          
          <div className="grid gap-6 md:grid-cols-3">
            {test.tests?.map((t, index) => (
              <div key={t._id} className="overflow-hidden transition-shadow bg-white shadow-md rounded-xl hover:shadow-xl">
                <div className="p-4 bg-gradient-to-r from-[#EB5C45] to-[#F59349]">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">{t.test_name}</h3>
                    <span className="px-3 py-1 text-sm font-semibold bg-white rounded-full text-regal-blue">
                      Test #{index + 1}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex gap-6 mb-4">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-regal-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-700"><strong>{t.duration}</strong> mins</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-regal-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-700"><strong>{t.total_marks}</strong> marks</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="space-y-2">
                      {t.subjects?.map((s) => (
                        <div key={s._id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                          <span className="font-medium text-gray-800">{s.subject_name}</span>
                          <span className="px-3 py-1 text-sm font-semibold rounded-full bg-regal-yellow text-regal-blue">
                            {s.question_count} Q's
                          </span>
                        </div>
                      ))}
                    </div>
                    {!userdetails ? (
                      <Link to={"/login"}>
                        <button className="flex gap-2 px-4 py-2 mt-5 font-bold text-white transition-all duration-200 transform shadow-lg bg-gradient-to-r from-red-600 to-orange-600 rounded-xl hover:from-red-700 hover:to-orange-700">
                          <Lock/> Login to Attend
                        </button>
                      </Link>
                    ) : !hasPurchased ? (
                      <button 
                        onClick={handleBuyNow}
                        className="flex items-center justify-center w-full gap-2 px-4 py-2 mt-5 font-bold text-white transition-all duration-200 transform shadow-lg bg-gradient-to-r from-red-600 to-orange-600 rounded-xl hover:from-red-700 hover:to-orange-700"
                      >
                        <Lock/> Purchase to Unlock
                      </button>
                    ) : (
                      <div className="flex gap-3 mt-5">
                        <button onClick={() => handleStartTest(t._id)} 
                          className="w-full bg-regal-blue hover:bg-gradient-to-l hover:from-regal-yellow hover:to-regal-blue text-white font-medium py-2.5 rounded-lg transition-all duration-200"
                        >
                          {testAttempts[t._id] ? 'Re Attend' : 'Attend Now'}
                        </button>

                        {testAttempts[t._id] && (
                          <button className="w-full bg-green-600 hover:bg-green-700 text-center text-white font-medium py-2.5 rounded-lg transition-all duration-200 px-4" 
                            onClick={() => setResultsModal(true)}
                          >
                            Results
                          </button>
                        )}
                        
                        {resultsModal && (
                          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="relative w-full max-w-md p-6 mx-4 bg-white shadow-lg rounded-2xl">
                              <div className="flex justify-between mb-5">
                                <h2 className="text-xl font-semibold">View Results</h2>
                                <button onClick={() => setResultsModal(false)}>
                                  <X className="w-5 h-5" />
                                </button> 
                              </div>
                              {testAttempts[t._id] && testAttempts[t._id].length > 0 && (
                                <div className="space-y-2">
                                  {testAttempts[t._id].map((attempt, idx) => (
                                    <button key={attempt._id} 
                                      onClick={() => navigate('/test-result', { state: { attemptId: attempt._id,testSeriesSlug: slug }})}
                                      className="w-full bg-green-600 hover:bg-green-700 text-center text-white font-medium py-2.5 rounded-lg transition-all duration-200 px-4"
                                    >
                                      View Result - Attempt {idx + 1} 
                                    </button>
                                  ))}
                                  <div className="flex justify-end pt-3">
                                    <button onClick={() => setResultsModal(false)} className="px-4 py-2 text-black bg-gray-300 rounded-lg hover:bg-gray-400">
                                      Close
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}